import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import useTheme4A from '../components/useTheme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function ResetPasswordPage() {
  useTheme4A();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [token, setToken] = useState('');
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      setIsValid(true);
    } else {
      toast.error('Invalid reset link');
      setTimeout(() => navigate('/login'), 2000);
    }
  }, [searchParams, navigate]);

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('All fields required');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          new_password: newPassword
        })
      });

      if (response.ok) {
        toast.success('Password reset successfully');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('An error occurred');
    }
  };

  if (!isValid) {
    return (
      <div className="min-h-screen bg-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <p>Invalid reset link. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-600 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <img src={AXIOM_LOGO} alt="Axiom" className="h-20 mx-auto" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold tracking-tight text-white text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Reset Password
        </h1>
        <p className="text-base font-normal text-blue-100 leading-relaxed text-center mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
          Enter your new password below
        </p>

        {/* Form */}
        <div className="bg-white rounded-md p-6 space-y-4">
          <div>
            <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">New Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12 pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Confirm Password</Label>
            <div className="relative">
              <Input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12 pr-10"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
                data-testid="toggle-confirm-visibility"
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            onClick={handleResetPassword}
            className="w-full bg-zinc-950 text-white hover:bg-zinc-800 rounded-md h-12 font-medium text-sm uppercase tracking-wide"
            data-testid="reset-password-btn"
          >
            Reset Password
          </Button>

          <button
            onClick={() => navigate('/login')}
            className="w-full text-sm text-zinc-600 hover:underline text-center"
            data-testid="back-to-login-btn"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
